/**
 * Book stickers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stickers`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const bookStickersPack: ToilePack = {
    slug: 'book-stickers',
    category: 'sticker',
    name: 'Book stickers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'stack', label: 'Book stack', subject: 'a stack of four books of different sizes'},
        {key: 'open', label: 'Open book', subject: 'an open book with a ribbon bookmark and pages fanning'},
        {key: 'mug', label: 'Mug on a book', subject: 'a mug of tea resting on a closed book'},
    ],
    styles: [
        {key: 'retro', label: 'Retro', style: 'retro sticker illustration, 1970s shapes, halftone grain, slight misregistration', palette: 'Mustard, rust, teal and cream', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'bold', label: 'Bold cartoon', style: 'bold cartoon sticker, thick black outlines, bright flat fills, slight gloss', palette: 'Bright saturated primaries with black outlines', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'minimal', label: 'Minimal line', style: 'minimal single-line sticker, one clean continuous line, no fill', palette: 'Black line only, one accent colour', framing: 'Each sticker isolated with a thin white die-cut border on a plain ground'},
    ],
    directions: ['a library card instead', 'for a journal cover', 'as a bookmark pair'],
    directionHint: 'try: a library card instead',
    seo: {
        title: 'Book Stickers: Stack, Open Book, Mug',
        metaDescription: 'Nine reading stickers — a book stack, an open book with a bookmark, a mug on a book — in retro, bold cartoon, and minimal line styles.',
        h1: 'Book stickers: book stack, open book, mug and book',
        tagline: 'Nine stickers, three reading moments, three styles each.',
        intro: 'A stack of four books of different sizes, an open book with a ribbon bookmark and fanning pages, and a mug of tea resting on a closed book — three reading stickers, die-cut and ready to print. Each rendered retro, bold cartoon, and minimal single-line. Ask for a specific genre, a reading-nook scene, or a library composition, and the same three styles carry it.',
        useCases: [
            'a water bottle or laptop lid',
            'a planner or reading journal',
            'a bookmark or tote bag',
            'a gift for a reader',
        ],
        faq: [
            {q: "What's in this pack?", a: 'Nine book stickers — a book stack, an open book with a bookmark, and a mug on a book — in retro, bold cartoon, and minimal line finishes.'},
            {q: 'Can I get a specific genre or reading scene?', a: 'Yes — the direction field takes any variation, including a specific genre, a reading-nook scene, or a library composition.'},
            {q: 'What size should I print these at?', a: 'Each sticker is a square die-cut design, so it scales cleanly from a small vinyl sticker to a full A4 sheet.'},
            {q: 'Do these show a specific book title?', a: 'No — the books are drawn with blank spines and covers, so nothing infringes on a real title or cover design.'},
        ],
    },
}
